// Server-time hooks for the player screens. Every moment that matters (pre-roll end, deadline, board switch, next question)
// is a server timestamp; the phone converts with the measured clock offset (serverNow) and re-renders exactly then.
import { useEffect, useReducer, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { serverNow } from '@/lib/api';

/** Re-renders the caller when server time crosses each of the given moments. */
export function useRerenderAt(...times: Array<number | undefined>) {
  const [, force] = useReducer((n: number) => n + 1, 0);
  const key = times.map((t) => t ?? '').join(',');
  useEffect(() => {
    const now = serverNow();
    const ids = times.filter((t): t is number => typeof t === 'number' && t > now).map((t) => window.setTimeout(force, Math.min(t - now + 20, 2_147_000_000)));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Calls `onSecond(left)` each time the whole seconds left on the clock change during the answer window:
 * 19, 18 … 1, then 0 at the deadline. Nothing fires during the pre-roll.
 */
export function useSecondTicker(startsAt: number, deadlineAt: number, onSecond: (left: number) => void) {
  const cb = useRef(onSecond); cb.current = onSecond;
  useEffect(() => {
    let t: number | undefined; let stopped = false;
    const schedule = () => {
      if (stopped) return;
      const now = serverNow(); const msLeft = deadlineAt - now;
      const k = Math.ceil(msLeft / 1000) - 1; // the next boundary strictly ahead: deadline − k seconds
      if (k < 0) return;
      const at = deadlineAt - k * 1000;
      t = window.setTimeout(() => { if (serverNow() >= startsAt - 30) cb.current(k); schedule(); }, Math.max(0, at - now));
    };
    schedule();
    return () => { stopped = true; if (t) window.clearTimeout(t); };
  }, [startsAt, deadlineAt]);
}

/** Whole seconds until a server moment (rounded up), ticking once a second; undefined when there is no moment. */
export function useSecondsUntil(at: number | undefined): number | undefined {
  const calc = () => (at === undefined ? undefined : Math.max(0, Math.ceil((at - serverNow()) / 1000)));
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    setLeft(calc());
    if (at === undefined) return;
    let t: number | undefined;
    const loop = () => { const ms = at - serverNow(); setLeft(Math.max(0, Math.ceil(ms / 1000))); if (ms > 0) t = window.setTimeout(loop, ((ms % 1000) || 1000) + 10); };
    loop();
    return () => { if (t) window.clearTimeout(t); };
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps
  return left;
}

/** "4:05", "1:04:05"; for a lobby countdown. */
export function clockLabel(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds)); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m); const ss = String(r).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * The day of a start in the viewer's zone, when it is not today: "tomorrow", or "Thu 15 Oct" further off; '' for today.
 * Times stay in the viewer's own zone (a student in Tbilisi reads 22:10 where London reads 19:10); the day is added so a
 * start on another day never reads as today's (14 Oct 2026: a test copy's lobby said "Starts at 06:00" for tomorrow).
 */
export function startDay(at: number, now: number = Date.now()): string {
  try {
    const day = (d: Date) => d.toDateString();
    const when = new Date(at);
    if (day(when) === day(new Date(now))) return '';
    const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
    if (day(when) === day(tomorrow)) return 'tomorrow';
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).format(when);
  } catch { return ''; }
}
/** "Starts at", "Starts tomorrow at", "Starts Thu 15 Oct at": the words before a start's time of day. */
export const startsAtLabel = (at: number, now?: number) => { const d = startDay(at, now); return d ? `Starts ${d} at` : 'Starts at'; };

/** The viewer's zone at that moment, as British English names it: "BST" or "GMT" in the UK, "GMT+4" in Tbilisi; '' if unknown. */
export function zoneName(at: number): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' }).formatToParts(new Date(at)).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch { return ''; }
}
/** After a start's time of day (14 Oct 2026): "your time (GMT+4)", or "your time" where the zone has no name. */
export const yourTime = (at: number) => { const z = zoneName(at); return z ? `your time (${z})` : 'your time'; };

/** Wall-clock time of a server moment in the viewer's zone, "19:30". */
export function timeOfDay(at: number): string {
  try { return new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}

export function prefersReducedMotion(): boolean {
  try { return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches); } catch { return false; }
}

/**
 * True when the viewer asked for less motion. Motion's own hook caches the first answer for the page; this one also reads
 * the media query on every render, so a change of setting (or a test) is honoured at once.
 */
export function useCalm(): boolean {
  const cached = useReducedMotion();
  return Boolean(cached) || prefersReducedMotion();
}
